import { defineComponent } from 'vue';
import { QTooltip, QIcon, QBadge, QMenu, QList, QSeparator } from 'quasar';
import BaseComponent from '../BaseComponentMixin.jsx';
import utils from '../../../Shared/utils.jsx';
import NavDrawerMenuItem from './NavDrawerMenuItem.jsx';

function normalizeIcon(icon) {
    if (typeof icon !== 'string')
        return icon || '';
    return icon.startsWith('mdi ') ? icon.substring(4) : icon;
}

const NavDrawerIcon = defineComponent({
    name: 'NavDrawerIcon',
    extends: BaseComponent,
    components: {
        QTooltip,
        QIcon,
        QBadge,
        QMenu,
        QList,
        QSeparator,
        NavDrawerMenuItem,
    },
    emits: ['update:modelValue', 'select', 'menu-action', 'action', 'finished-render'],
    props: {
        item: { type: Object, required: true },
        rowData: { type: Object, default: null },
        expanded: { type: Boolean, default: false },
        modelValue: { type: [String, Number], default: null },
        dense: { type: Boolean, default: false },
        previewMode: { type: Boolean, default: false },
    },
    data() {
        return {
            iconexpn: null,
            tooltipexpn: null,
            menuOpen: false,
            menuAnchor: null,
        };
    },
    computed: {
        data() {
            return this.rowData;
        },
        icon() {
            const iconExpr = this.controlData?.Icon?.Icon ?? this.rowData?.Icon?.Icon;
            if (iconExpr) {
                try {
                    if (!this.iconexpn)
                        this.iconexpn = utils.compile(this, iconExpr);
                    return normalizeIcon(String(utils.evaluate(this.iconexpn, this)));
                }
                catch (e) {
                    return normalizeIcon(this.item?.icon || 'mdi-circle-outline');
                }
            }

            return normalizeIcon(this.item?.icon || 'mdi-circle-outline');
        },
        /**
         * Hover tooltip — Vue2 NavigationDrawerItem uses Icon.Tooltip then Translate().
         */
        tooltip() {
            const tooltipExpr = this.controlData?.Icon?.Tooltip
                ?? this.controlData?.Title
                ?? this.rowData?.Icon?.Tooltip
                ?? this.rowData?.Title;
            if (tooltipExpr) {
                try {
                    if (!this.tooltipexpn)
                        this.tooltipexpn = utils.compile(this, tooltipExpr);
                    const value = String(utils.evaluate(this.tooltipexpn, this) ?? '');
                    return typeof this.Translate === 'function' ? this.Translate(value) : value;
                }
                catch (e) {
                    const fallback = this.item?.tooltip ?? '';
                    return typeof this.Translate === 'function' ? this.Translate(fallback) : fallback;
                }
            }
            const fallback = this.item?.tooltip ?? '';
            return typeof this.Translate === 'function' ? this.Translate(fallback) : fallback;
        },
        /**
         * Expanded drawer label — Vue2 NavigationDrawerItem shows
         * Translate(Icon.Tooltip) as the visible title (Title is the MenuItem
         * document name, so prefer tooltip for display parity).
         */
        label() {
            // Same precedence as tooltip() / Vue2's visible v-list-item-title.
            return this.tooltip || this.item?.label || '';
        },
        isActive() {
            if (this.item?.type !== 'tab')
                return false;
            return String(this.modelValue) === String(this.item?.id);
        },
        menuChildren() {
            return Array.isArray(this.rowData?.ChildData) ? this.rowData.ChildData : [];
        },
        badge() {
            return this.item?.badge ?? this.controlData?.badgecontent;
        },
    },
    methods: {
        preRenderComplete() {
            this.finishRenderHandler(this);
        },
        onItemClick(event) {
            if (this.previewMode) {
                if (this.item?.id === '__collapse__' && typeof this.item?._navAction === 'function')
                    this.item._navAction();
                return;
            }
            if (this.item?.type === 'menu') {
                this.menuOpen = !this.menuOpen;
                this.menuAnchor = event?.currentTarget ?? event?.target ?? this.menuAnchor;
                return;
            }

            if (this.item?.type === 'action') {
                if (typeof this.item?._navAction === 'function') {
                    this.$emit('action', { item: this.item, handled: false });
                    return;
                }

                if (this.rowData?.Actions)
                    utils.executeAndCompileAllActions(this.rowData.Actions, null, this);

                this.$emit('action', { item: this.item, handled: true });
                return;
            }

            if (this.item?.type === 'tab') {
                this.$emit('update:modelValue', this.item.id);
                this.$emit('select', this.item.id);
            }
        },
        onMenuAction(payload) {
            this.$emit('menu-action', { item: this.item, ...payload });
        },
        onMenuOpenChanged(value) {
            this.menuOpen = !!value;
        },
    },
    render() {
        if (!this.todisplay)
            return null;

        const buttonClass = {
            'icon-drawer__item': true,
            'icon-drawer__item--active': this.item?.type === 'tab' && this.isActive,
            'icon-drawer__item--menu': this.item?.type === 'menu',
        };
        const labelClass = {
            'icon-drawer__label': true,
            'icon-drawer__label--visible': this.expanded,
        };
        const label = this.label;
        const tooltip = this.tooltip || label;
        const isBottomPlacement = this.item?.placement === 'bottom';
        const menuAnchor = isBottomPlacement ? 'bottom right' : 'top right';
        const menuSelf = isBottomPlacement ? 'bottom left' : 'top left';
        const menuOffset = [2, 40];
        const childNodes = [];
        if (this.item?.type === 'menu' && this.menuChildren.length) {
            for (let i = 0; i < this.menuChildren.length; i++) {
                const child = this.menuChildren[i];
                if (!child.$objectid)
                    child.$objectid = utils.generateUUID();

                if (child.Divider) {
                    // No spaced margins — keeps Sign Out / Language dividers flush
                    // with adjacent items (CCDEV-17617 / Oliwia design review).
                    childNodes.push(
                        <QSeparator key={`${child.$objectid}_divider`} />
                    );
                }

                childNodes.push(
                    <NavDrawerMenuItem
                        key={child.$objectid}
                        class={{
                            'c-NavDrawerMenuItem': true,
                            [`c-name-${(this.name + '_child_' + i) || 'unnamed'}`]: true,
                        }}
                        child={child}
                        name={this.name ? `${this.name}_child_${i}` : ''}
                        root={this.root}
                        type="NavDrawerMenuItem"
                        parentType="NavDrawerIcon"
                        controlData={child.Data?.MenuItemData ?? {}}
                        scopeitems={this.scopeitems}
                        controlscope={this.controlscope}
                        dense={this.dense}
                        onMenuAction={this.onMenuAction}
                        onFinishedRender={(reference) => this.finishRenderHandler(reference)}
                    />
                );
            }
        }

        return (
            <button
                type="button"
                class={buttonClass}
                aria-pressed={this.item?.type === 'tab' ? this.isActive : undefined}
                aria-label={label || tooltip || this.icon}
                onClick={this.onItemClick}
            >
                {tooltip && !this.menuOpen && (
                    <QTooltip delay={400} anchor="center right" self="center left" transition-show="scale" transition-hide="scale">
                        {tooltip}
                    </QTooltip>
                )}

                <span class="icon-drawer__icon-wrap relative-position" style={{ display: 'inline-flex', flexShrink: 0 }}>
                    <QIcon name={this.icon} size="sm" />
                    {this.badge?.showbadge && this.badge?.count > 0 && (
                        <QBadge
                            floating rounded
                            class="icon-drawer__badge"
                            style={{ top: '-12px', right: '-12px', transform: 'none' }}
                            label={this.badge.count < 100 ? String(this.badge.count) : '99+'}
                            color="negative"
                        />
                    )}
                </span>

                {this.expanded && (
                    <span class={labelClass}>{label}</span>
                )}

                {this.item?.type === 'menu' && this.menuChildren.length > 0 && (
                    <QMenu
                        modelValue={this.menuOpen}
                        xonUpdate:modelValue={this.onMenuOpenChanged}
                        xtarget={this.menuAnchor}
                        xanchor={menuAnchor}
                        anchor="bottom right"
                        self="bottom left"
                        xself={menuSelf}
                        offset={menuOffset}
                        noParentEvent
                        autoClose
                        transitionShow="slide-up"
                        transitionHide="slide-down"
                    >
                        <QList dense style="min-width: 160px">
                            {childNodes}
                        </QList>
                    </QMenu>
                )}
            </button>
        );
    },
});

export default NavDrawerIcon;
